import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Pagination,
  Row,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaSearch, FaSyncAlt, FaUsers } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebGroups,
  listWhatsAppWebChannels,
  syncWhatsAppWebGroups,
  enrichWhatsAppWebGroups,
} from "../../../../services/api";

const PAGE_SIZE = 30;

export default function GroupsPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [waSyncing, setWaSyncing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listWhatsAppWebGroups({
        page,
        pageSize: PAGE_SIZE,
        search,
        channelId: channelFilter || null,
      });
      const loaded = res?.data || [];
      setGroups(loaded);
      setPagination(res?.pagination || null);

      // Enrichment automático: pra grupos sem foto, busca live no WhatsApp
      // (estilo OrbitSender groups-enriched). Agrupado por canal pra fazer
      // 1 request por canal ativo.
      const needEnrich = loaded.filter(
        (g) => !g.profile_picture_url && g.channel?.id
      );
      const byChannel = needEnrich.reduce((acc, g) => {
        const cid = g.channel.id;
        if (!acc[cid]) acc[cid] = [];
        acc[cid].push(g.whatsapp_jid);
        return acc;
      }, {});

      Object.entries(byChannel).forEach(async ([cid, jids]) => {
        try {
          const enriched = await enrichWhatsAppWebGroups(cid, jids);
          const data = enriched?.data || {};
          setGroups((prev) =>
            prev.map((g) => {
              const e = data[g.whatsapp_jid];
              if (!e) return g;
              return {
                ...g,
                profile_picture_url: e.picture || g.profile_picture_url,
                members_count: e.participants ?? g.members_count,
              };
            })
          );
        } catch (_) {
          // canal pode estar inativo — ignora silenciosamente
        }
      });
    } finally {
      setLoading(false);
    }
  }, [page, search, channelFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    listWhatsAppWebChannels().then((res) =>
      setChannels(res?.data?.channels || [])
    );
  }, []);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  async function handleSync() {
    if (!channelFilter) {
      alert("Selecione um canal antes de sincronizar.");
      return;
    }
    setSyncing(true);
    setWaSyncing(false);
    try {
      const res = await syncWhatsAppWebGroups(channelFilter);
      // Backend retorna { syncing: true } quando o WA Multi-Device ainda
      // não terminou o sync inicial — auto-retry em 5s.
      if (res?.syncing) {
        setWaSyncing(true);
        setTimeout(() => handleSync(), 5000);
        return;
      }
      setWaSyncing(false);
      await reload();
    } catch (err) {
      alert("Falha ao sincronizar. Confirme que o canal está ativo.");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h2 className="mb-1">
                <FaUsers className="mr-2" />
                Grupos em Tempo Real
              </h2>
              <p className="text-muted mb-0">
                Grupos carregados diretamente do WhatsApp do canal
              </p>
            </div>
          </div>

          {/* Filtros */}
          <Card className="mb-3">
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Label>Filtrar por Canal</Form.Label>
                  <Form.Control
                    as="select"
                    value={channelFilter}
                    onChange={(e) => {
                      setChannelFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">Todos os canais</option>
                    {channels.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.status === "active" ? "✅" : "⚪"}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md={6}>
                  <Form onSubmit={handleSearch}>
                    <Form.Label>Pesquisar Grupos</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nome..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <InputGroup.Append>
                        <Button type="submit" variant="primary">
                          <FaSearch />
                        </Button>
                      </InputGroup.Append>
                    </InputGroup>
                  </Form>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="success"
                    onClick={handleSync}
                    disabled={syncing}
                    className="w-100"
                  >
                    <FaSyncAlt /> {syncing ? "Sincronizando..." : "Sincronizar"}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {waSyncing && (
            <Card className="mb-3 border-warning">
              <Card.Body className="text-warning d-flex align-items-center">
                <Spinner animation="border" size="sm" className="mr-2" />
                <span>
                  <strong>Aguardando sincronização do WhatsApp Web...</strong>{" "}
                  O Multi-Device leva ~20s pra carregar os chats depois que conecta.
                  Vou retentar automaticamente em 5s.
                </span>
              </Card.Body>
            </Card>
          )}

          <p className="text-muted">
            Exibindo {groups.length} de {pagination?.totalCount ?? 0} grupos
            {pagination?.totalPages > 0 &&
              ` (Página ${pagination.page} de ${pagination.totalPages})`}
          </p>

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : groups.length === 0 ? (
            <Card className="text-center p-5">
              <Card.Body>
                <p className="mb-2">Nenhum grupo encontrado.</p>
                <small className="text-muted">
                  Selecione um canal ativo e clique em "Sincronizar".
                </small>
              </Card.Body>
            </Card>
          ) : (
            <Row>
              {groups.map((g) => (
                <Col key={g.id} md={6} lg={4} className="mb-3">
                  <Card>
                    {g.profile_picture_url ? (
                      <Card.Img
                        variant="top"
                        src={g.profile_picture_url}
                        style={{ height: 140, objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          height: 140,
                          background: "rgba(255,255,255,0.05)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 48,
                        }}
                      >
                        👥
                      </div>
                    )}
                    <Card.Body>
                      <Card.Title style={{ fontSize: 14 }}>
                        {g.name || "(sem nome)"}
                      </Card.Title>
                      <Badge variant="info">{g.channel?.name}</Badge>
                      {g.is_owned_by_us && (
                        <Badge variant="success" className="ml-1">
                          Próprio
                        </Badge>
                      )}
                      {g.members_count != null && (
                        <small className="text-muted d-block mt-2">
                          {g.members_count} membros
                        </small>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {pagination && pagination.totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              />
              <Pagination.Item active>
                {page} / {pagination.totalPages}
              </Pagination.Item>
              <Pagination.Next
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              />
            </Pagination>
          )}
        </Col>
      </Row>
    </Container>
  );
}
